<template>
  <bj-modal
    id="add-inspections-dialog"
    v-model="addInspectionDialog.visible"
    size="auto"
    title="Add Inspections from CSV"
  >
    <div slot="content">
      <div class="row">
        <div class="col-12 mb-4">
          Import inspections from CSV file:
          <input
            type="file"
            placeholder="Import inspections from csv file"
            accept=".csv"
            @change="readCsvFile"
          >
        </div>
        <div class="col-12">
          <p>
            Please provide a CSV list in the following structure:
            <b>Property address, Business ID, PM email, Unit name,
              Unit ID, Special notes, Tenant first name, Tenant last name,
              Tenant phone, Tenant email (',' separated):</b>
          </p>
        </div>
      </div>
      <div class="row">
        <div class="col-12">
          <q-input
            v-model="csvString"
            inverted
            type="textarea"
            class="csv-input"
            :rows="4"
            @input="convertToJson"
          />
          <div
            v-if="errors.length"
            class="text-red"
          >
            <p
              v-for="(error, index) in errors"
              :key="`error_${index}`"
            >
              {{ error }}
            </p>
          </div>
          <template v-if="inspectionsData.length">
            <p>Inspections list:</p>
            <div class="inspections-list-row-wrapper">
              <div
                v-for="(inspection, index) in inspectionsData"
                :key="`inspection_${index}`"
              >
                <q-tooltip
                  v-show="inspection.error"
                  anchor="top left"
                  self="bottom middle"
                  :offset="[-100, 0]"
                >
                  {{ inspection.error }}
                </q-tooltip>
                <q-icon
                  v-if="inspection.added === null"
                  name="brightness_1"
                  color="gray"
                />
                <q-icon
                  v-else-if="inspection.added === true"
                  name="brightness_1"
                  color="green"
                />
                <q-icon
                  v-else-if="inspection.added === false"
                  name="error"
                  color="red"
                />
                <span>{{ getRowTitle(inspection) }}</span>
              </div>
            </div>
          </template>
        </div>
      </div>
      <div slot="footer">
        <div class="row justify-content-between">
          <div class="d-flex">
            <q-btn
              rounded
              label="Cancel"
              color="secondary"
              @click="$emit('close')"
            />
          </div>
          <div class="d-flex">
            <q-btn
              :disable="errors.length !== 0"
              rounded
              label="Add inspections"
              color="primary"
              @click="addInspections"
            />
          </div>
        </div>
      </div>
    </div>
  </bj-modal>
</template>

<script>
import config from '../../../plugins/bluejay/config';

let preInspectionData = [];
export default {
  name: 'AddInspectionsDialog',
  props: {
    addInspectionDialog: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      markets: null,
      csvString: null,
      errors: [],
      inspectionsData: [],
    };
  },
  watch: {
    value(value) {
      if (value) {
        this.csvString = null;
        this.errors = [];
        this.inspectionsData = [];
      }
    },
  },
  methods: {
    getRowTitle(inspection) {
      const tenants = [];
      inspection.tenants.map((tenant) => {
        tenants.push(`${tenant.first_name} ${tenant.last_name}`);
      });
      return `${inspection.address} - ${inspection.unit_name}, ${inspection.associated_member_email}, [${tenants.join(', ')}]`;
    },
    readCsvFile(event) {
      const file = event.target.files[0];
      const reader = new FileReader();
      reader.onload = () => {
        this.csvString = reader.result.replace(/\r\n/g, '\n');// make sure crlf converted to lf
        this.convertToJson();
      };
      reader.readAsText(file);
    },
    convertToJson() {
      this.inspectionsData = [];
      this.errors = [];
      preInspectionData = [];
      const tempAll = this.csvString;
      const tempRows = tempAll.split(new RegExp('\\n'));
      tempRows.shift();
      const inspections = [];
      tempRows.forEach((row) => {
        const arrFromRow = row.split(csvDelimiter);
        if (arrFromRow.length !== 10) {
          this.errors.push('Please, check the structure, some rows are incomplete');
          return false;
        }
        inspections.push(arrFromRow);
      });
      this.mapInspectionsData(inspections);
    },
    mapInspectionsData(array) {
      array.map((inspection) => {
        const [address, business_id, associated_member_email, unit_name, unit_id, instructions, first_name, last_name, phone_number, email] = inspection;
        const index = business_id + unit_id;
        if (!preInspectionData[index]) {
          preInspectionData[index] = {
            business_id,
            unit_id,
            instructions,
            associated_member_email: this.validateEmail(associated_member_email),
            unit_name,
            address,
            tenants: [{
              first_name,
              last_name,
              phone_number: this.trimAndValidatePhone(phone_number),
              email: this.validateEmail(email),
            }],
            added: null,
            type: config.inspectionTypes.PERIODIC,
          };
        } else {
          this.validateEmail(associated_member_email);
          preInspectionData[index].tenants.push({
            first_name,
            last_name,
            phone_number: this.trimAndValidatePhone(phone_number),
            email: this.validateEmail(email),
          });
        }
      });
      preInspectionData.map((inspection) => {
        this.inspectionsData.push(inspection);
      });
    },
    validateEmail(email) {
      if (!this.$config.emailRegex.test(email)) {
        this.errors.push(`Invalid email address: ${email}`);
      } else {
        return email;
      }
    },
    trimAndValidatePhone(phone) {
      const trimmedPhone = phone.replace(/[^0-9+]/g, '');
      if (!this.$config.phoneNumberRegex.test(trimmedPhone)) {
        this.errors.push(`Invalid phone number: ${phone}`);
      } else {
        return trimmedPhone;
      }
    },
    addInspections() {
      this.createInspection(0);
    },
    createInspection(index) {
      if (index === this.inspectionsData.length) {
        this.$emit('added');
        return;
      }
      return new Promise(() => {
        this.api().request('POST', 'admin/inspections', this.inspectionsData[index])
          .then(
            () => {
              this.inspectionsData[index].added = true;
              this.createInspection(index + 1);
            },
            (error) => {
              this.inspectionsData[index].added = false;
              this.inspectionsData[index].error = this.getFirstError(error) || error.message;
              this.createInspection(index + 1);
              this.$emit('added');
            },
          );
      });
    },
  },
};
const csvDelimiter = ',';
</script>

<style lang="scss" scoped>
.inspections-list-row-wrapper {
  div {
    flex: 25%;
    margin-bottom: 1rem;
  }

  span {
    margin-left: 0.5rem;
  }
}

.csv-input {
  margin: 1rem 0;
}
</style>
